import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

interface NotificationDto {
  id: number;
  group: string | null;
  bodyHtml: string;
  dismissable: boolean;
  displayAfter: string | null;
  createdAt: string | null;
}

const STORAGE_KEY = 'fs.dismissedNotifications';

function readDismissed(): Set<number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as number[];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function writeDismissed(ids: Set<number>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids)));
  } catch {
    /* ignore */
  }
}

export function NotificationBanners() {
  const q = useQuery({
    queryKey: ['notifications-active'],
    queryFn: () => api.get<NotificationDto[]>('/api/v1/notifications'),
    staleTime: 60_000,
    retry: false,
  });
  const [dismissed, setDismissed] = useState<Set<number>>(() => readDismissed());

  useEffect(() => { writeDismissed(dismissed); }, [dismissed]);

  const visible = useMemo(
    () => (q.data ?? []).filter((n) => !dismissed.has(n.id)),
    [q.data, dismissed]
  );

  if (visible.length === 0) return null;

  return (
    <div className="border-b border-slate-200 bg-amber-50">
      <div className="mx-auto max-w-6xl space-y-2 px-6 py-2">
        {visible.map((n) => (
          <div key={n.id} className="flex items-start gap-3 text-sm text-amber-900">
            <div
              className="prose prose-sm max-w-none flex-1"
              // bodyHtml is server-sanitised before persistence.
              dangerouslySetInnerHTML={{ __html: n.bodyHtml }}
            />
            {n.dismissable && (
              <button
                type="button"
                onClick={() => setDismissed((s) => new Set(s).add(n.id))}
                className="rounded-md border border-amber-300 bg-white px-2 py-0.5 text-xs text-amber-800 hover:bg-amber-100"
                aria-label="Dismiss"
              >
                Dismiss
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
